import React, { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/auth/AuthContext';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Settings,
  Activity,
  FileText,
  ScrollText,
  Key,
  LogOut,
  MessageSquare,
  User as UserIcon
} from 'lucide-react';

interface SidebarItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ to, icon, label }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex items-center space-x-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
        ? 'bg-primary/10 text-blue-400 border-l-2 border-blue-500'
        : 'text-text-muted hover:bg-[#1e293b] hover:text-gray-200'
      }`
    }
  >
    {icon}
    <span>{label}</span>
  </NavLink>
);

export const Sidebar: React.FC = () => {
  const { t } = useTranslation('common');
  const { logout, user } = useAuth();

  return (
    <aside className="fixed inset-s-0 top-0 h-screen w-64 bg-slate-nav border-e border-[#1e293b] flex flex-col z-50">
      {/* Brand */}
      <div className="p-6">
        <div className="flex items-center space-x-2 rtl:space-x-reverse text-primary">
          <Activity className="w-6 h-6" />
          <span className="text-xl font-bold tracking-tight text-white">Biombo</span>
        </div>
      </div>

      <div className="px-6 pb-2">
        <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">{t('backoffice.adminConsole')}</h3>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto custom-scrollbar">
        <SidebarItem to="/backoffice/dashboard" icon={<LayoutDashboard size={18} />} label={t('backoffice.sidebar.dashboard')} />
        <SidebarItem to="/backoffice/customers" icon={<Users size={18} />} label={t('backoffice.sidebar.customers')} />
        <SidebarItem to="/backoffice/whatsapp" icon={<MessageSquare size={18} />} label="WhatsApp" />
        <SidebarItem to="/plans" icon={<CreditCard size={18} />} label={t('backoffice.sidebar.billing')} />
        <SidebarItem to="/backoffice/settings" icon={<Settings size={18} />} label={t('backoffice.sidebar.settings')} />

        <div className="mt-8 mb-2 px-3">
          <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Context: Acme Corp</h3>
        </div>

        <SidebarItem to="#" icon={<Activity size={18} />} label={t('backoffice.sidebar.telemetryDetail')} />
        <SidebarItem to="#" icon={<FileText size={18} />} label={t('backoffice.sidebar.accountFiles')} />
        <SidebarItem to="#" icon={<ScrollText size={18} />} label={t('backoffice.sidebar.auditLogs')} />
        <SidebarItem to="#" icon={<Key size={18} />} label={t('backoffice.sidebar.apiKeys')} />
      </nav>

      {/* User Footer */}
      <div className="p-4 border-t border-[#1e293b] bg-[#0d141c]">
        {/* Plan Info */}
        <div className="bg-[#161f29] rounded-lg p-3 mb-4 border border-[#26313d]">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-text-muted">{t('backoffice.sidebar.planTier')}</span>
            <span className="text-[10px] bg-green-900/30 text-green-400 px-1.5 py-0.5 rounded border border-green-800/50">ENTERPRISE</span>
          </div>
          <div className="text-sm font-semibold text-white mb-1">$499.00 / mo</div>
          <div className="text-[10px] text-text-muted mb-3">Next bill: Oct 24, 2023</div>
          <button className="w-full py-1.5 text-xs font-medium text-gray-300 bg-[#1e293b] hover:bg-[#2a3848] rounded border border-[#334155] transition-colors">
            {t('backoffice.sidebar.manageSubscription')}
          </button>
        </div>

        {/* User Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 rtl:space-x-reverse overflow-hidden">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {user?.email ? user.email.substring(0, 2).toUpperCase() : 'AD'}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-white truncate">{user?.name || 'Admin User'}</span>
              <span className="text-xs text-text-muted truncate" title={user?.email || ''}>{user?.email}</span>
            </div>
          </div>
          <button
            onClick={logout}
            className="text-text-muted hover:text-red-400 transition-colors p-1"
            title={t('backoffice.sidebar.signOut')}
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
};
